import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import { transportMeansUnitFromRow } from "../../../../../CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  getAllTransportToManufacturingLCA,
  addTransportToManufacturingLCA,
  updateTransportToManufacturingLCA,
  deleteTransportToManufacturingLCA,
} from "../../../../../../apis/Product/TransportA2Api";
import {
  getProductTransportMeansDropdown,
  getProductSurplusTransportCountryDropdown,
  getTransportPackagingFromA1Dropdown,
} from "../../../../../../apis/Dropdown";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { TableCompareTrigger } from "../../../components/lcaComparison";

// Impact categories configuration
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  { name: "Eutrophication, terrestrial", unit: "mol N-eq", key: "eutrophication_terrestrial" },
  { name: "Eutrophication, freshwater", unit: "kg P-eq", key: "eutrophication_freshwater" },
  { name: "Eutrophication, marine", unit: "kg N-eq", key: "eutrophication_marine" },
  { name: "Human toxicity, cancer effects", unit: "CTUh", key: "carcinogenics" },
  { name: "Human toxicity, non-cancer effects", unit: "CTUh", key: "non_carcinogenics" },
  { name: "Particulate matter", unit: "disease incidence", key: "respiratory_effects" },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  { name: "Resource use, energy carriers", unit: "MJ", key: "fossil_fuel_depletion" },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  { name: "Resource use, mineral and metals", unit: "kg Sb-eq", key: "resource_use_minerals_metals" },
  { name: "Ionising radiation, human health", unit: "kBq U235-eq", key: "ionising_radiation" },
];

const TransportToManufacturingTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] = useState(0);
  const [transportMeansDropdown, setTransportMeansDropdown] = useState([]);
  const [countryDropdown, setCountryDropdown] = useState([]);
  const [packagingDropdown, setPackagingDropdown] = useState([]);
  const [unitTransportMeans, setUnitTransportMeans] = useState({});

  // Fetch dropdowns
  useEffect(() => {
    getProductTransportMeansDropdown({
      setDropdown: setTransportMeansDropdown,
    });
    getProductSurplusTransportCountryDropdown({
      setDropdown: setCountryDropdown,
    });
    if (productId) {
      getTransportPackagingFromA1Dropdown({
        productId,
        setDropdown: setPackagingDropdown,
      });
    }
  }, [productId]);

  // Convert country codes to names and packaging numeric values to dropdown options when dropdowns are loaded
  useEffect(() => {
    if (data.length > 0 && (countryDropdown.length > 0 || packagingDropdown.length > 0)) {
      setData((prevData) => {
        let hasChanges = false;
        const convertedData = prevData.map((item) => {
          const updates = {};
          
          // Convert departure_country code to name
          if (item.departure_country && typeof item.departure_country === "string") {
            if (item.departure_country.length <= 3) {
              const foundCountry = countryDropdown.find(
                (option) => option.code === item.departure_country
              );
              if (foundCountry && item.departure_country !== foundCountry.name) {
                hasChanges = true;
                updates.departure_country = foundCountry.name;
              }
            }
          }
          
          // Convert destination_country code to name
          if (item.destination_country && typeof item.destination_country === "string") {
            if (item.destination_country.length <= 3) {
              const foundCountry = countryDropdown.find(
                (option) => option.code === item.destination_country
              );
              if (foundCountry && item.destination_country !== foundCountry.name) {
                hasChanges = true;
                updates.destination_country = foundCountry.name;
              }
            }
          }
          
          // Convert packaging_included numeric value to match dropdown option
          // Backend returns packaging_included as a number (qty), but dropdown needs to match the option's value property
          if (item.packaging_included !== null && item.packaging_included !== undefined && packagingDropdown.length > 0) {
            const packagingValue = typeof item.packaging_included === "number" 
              ? item.packaging_included 
              : parseFloat(item.packaging_included);
            
            if (!isNaN(packagingValue) && packagingValue > 0) {
              // Find matching packaging option by qty value (with tolerance for floating point)
              const foundPackaging = packagingDropdown.find(
                (option) => {
                  const optionQty = option.qty || option.value || 0;
                  const optionValue = option.value || option.qty || 0;
                  // Match by qty (what backend stores) or value (what dropdown uses)
                  const matchesQty = Math.abs(optionQty - packagingValue) < 0.001;
                  const matchesValue = Math.abs(optionValue - packagingValue) < 0.001;
                  return matchesQty || matchesValue;
                }
              );
              
              if (foundPackaging) {
                // Store the dropdown option's value property so dropdown can match and display it
                const matchingValue = foundPackaging.value !== undefined ? foundPackaging.value : foundPackaging.qty;
                // Always set to dropdown option's value to ensure dropdown can match it
                // Check if value needs update (different value or different type)
                const currentValue = item.packaging_included;
                const needsUpdate = currentValue !== matchingValue || 
                                   typeof currentValue !== typeof matchingValue ||
                                   (typeof currentValue === "number" && typeof matchingValue === "number" && 
                                    Math.abs(currentValue - matchingValue) >= 0.001);
                
                if (needsUpdate) {
                  hasChanges = true;
                  updates.packaging_included = matchingValue;
                }
              }
            }
          }
          
          return hasChanges ? { ...item, ...updates } : item;
        });
        return hasChanges ? convertedData : prevData;
      });
    }
  }, [data, countryDropdown, packagingDropdown]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  // Update units when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && transportMeansDropdown.length > 0) {
      const newUnitTransportMeans = {};
      data.forEach((row) => {
        const transportMeansCode =
          typeof row.transport_means === "string"
            ? row.transport_means
            : row.transport_means?.value || row.transport_means?.code;
        if (transportMeansCode) {
          const selectedMeans = transportMeansDropdown.find(
            (option) =>
              !option.isCategory &&
              (option.code === transportMeansCode ||
                option.value === transportMeansCode)
          );
          if (selectedMeans) {
            newUnitTransportMeans[row.id] = selectedMeans.unit || "kgCO₂e/tkm";
          }
        }
      });
      setUnitTransportMeans(newUnitTransportMeans);
    }
  }, [data, transportMeansDropdown]);

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const fetchData = useCallback(() => {
    if (productId) {
      getAllTransportToManufacturingLCA({
        productId,
        setObjects: setData,
      });
    }
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = useCallback(
    (newRow) => {
      // Extract country codes - convert name to code if needed
      let departureCountry = null;
      if (newRow.departure_country) {
        const countryValue =
          typeof newRow.departure_country === "object"
            ? newRow.departure_country?.value || newRow.departure_country?.name
            : newRow.departure_country;
        const foundCountry = countryDropdown.find(
          (option) =>
            option.name === countryValue ||
            option.code === countryValue ||
            option.value === countryValue
        );
        departureCountry = foundCountry?.code || countryValue;
      }
      
      let destinationCountry = null;
      if (newRow.destination_country) {
        const countryValue =
          typeof newRow.destination_country === "object"
            ? newRow.destination_country?.value || newRow.destination_country?.name
            : newRow.destination_country;
        const foundCountry = countryDropdown.find(
          (option) =>
            option.name === countryValue ||
            option.code === countryValue ||
            option.value === countryValue
        );
        destinationCountry = foundCountry?.code || countryValue;
      }

      // Extract transport means code
      const transportMeans =
        typeof newRow.transport_means === "string"
          ? newRow.transport_means
          : newRow.transport_means?.code || newRow.transport_means?.value || null;

      // Handle packaging - can be from dropdown (object with qty/value) or manual number
      // Dropdown may pass: the value directly (number), the option object, or the value property
      let packagingIncluded = 0;
      if (newRow.packaging_included !== null && newRow.packaging_included !== undefined) {
        if (typeof newRow.packaging_included === "object") {
          // Dropdown selection returns object with qty or value property
          packagingIncluded = newRow.packaging_included.qty || newRow.packaging_included.value || 0;
        } else if (typeof newRow.packaging_included === "number") {
          // Direct number value - could be from dropdown value or manual input
          packagingIncluded = newRow.packaging_included;
        } else if (typeof newRow.packaging_included === "string") {
          // String value - try to parse as number first
          const parsed = parseFloat(newRow.packaging_included);
          if (!isNaN(parsed)) {
            packagingIncluded = parsed;
          } else {
            // If not a valid number, try to find matching option by value string
            const matchingOption = packagingDropdown.find(
              (option) => String(option.value) === newRow.packaging_included || String(option.qty) === newRow.packaging_included
            );
            if (matchingOption) {
              packagingIncluded = matchingOption.qty || matchingOption.value || 0;
            }
          }
        }
      }
      
      // Final fallback: if still 0 or undefined, try to find matching option
      // This handles cases where the dropdown passes something unexpected
      if ((packagingIncluded === 0 || packagingIncluded === undefined) && newRow.packaging_included !== null && newRow.packaging_included !== undefined) {
        const matchingOption = packagingDropdown.find(
          (option) => {
            const inputValue = newRow.packaging_included;
            return option.value === inputValue || 
                   option.qty === inputValue ||
                   String(option.value) === String(inputValue) ||
                   String(option.qty) === String(inputValue);
          }
        );
        if (matchingOption) {
          packagingIncluded = matchingOption.qty || matchingOption.value || 0;
        }
      }

      addTransportToManufacturingLCA({
        productId,
        transport_means: transportMeans,
        departure_city: newRow.departure_city || null,
        departure_country: departureCountry,
        destination_city: newRow.destination_city || null,
        destination_country: destinationCountry,
        transport_distance: newRow.transport_distance || 0,
        fu_transported: newRow.fu_transported || 0,
        packaging_included: packagingIncluded,
        manual_emission_factor: newRow.manual_emission_factor || null,
        callback: fetchData,
      });
    },
    [productId, fetchData, packagingDropdown, countryDropdown]
  );

  const handleEdit = useCallback(
    (updatedRow) => {
      // Extract country codes - convert name to code if needed
      let departureCountry = null;
      if (updatedRow.departure_country) {
        const countryValue =
          typeof updatedRow.departure_country === "object"
            ? updatedRow.departure_country?.value || updatedRow.departure_country?.name
            : updatedRow.departure_country;
        const foundCountry = countryDropdown.find(
          (option) =>
            option.name === countryValue ||
            option.code === countryValue ||
            option.value === countryValue
        );
        departureCountry = foundCountry?.code || countryValue;
      }
      
      let destinationCountry = null;
      if (updatedRow.destination_country) {
        const countryValue =
          typeof updatedRow.destination_country === "object"
            ? updatedRow.destination_country?.value || updatedRow.destination_country?.name
            : updatedRow.destination_country;
        const foundCountry = countryDropdown.find(
          (option) =>
            option.name === countryValue ||
            option.code === countryValue ||
            option.value === countryValue
        );
        destinationCountry = foundCountry?.code || countryValue;
      }

      // Extract transport means code
      const transportMeans =
        typeof updatedRow.transport_means === "string"
          ? updatedRow.transport_means
          : updatedRow.transport_means?.code || updatedRow.transport_means?.value || null;

      // Handle packaging - can be from dropdown (object with qty/value) or manual number
      // Dropdown may pass: the value directly (number), the option object, or the value property
      let packagingIncluded = 0;
      if (updatedRow.packaging_included !== null && updatedRow.packaging_included !== undefined) {
        if (typeof updatedRow.packaging_included === "object") {
          // Dropdown selection returns object with qty or value property
          packagingIncluded = updatedRow.packaging_included.qty || updatedRow.packaging_included.value || 0;
        } else if (typeof updatedRow.packaging_included === "number") {
          // Direct number value - could be from dropdown value or manual input
          packagingIncluded = updatedRow.packaging_included;
        } else if (typeof updatedRow.packaging_included === "string") {
          // String value - try to parse as number first
          const parsed = parseFloat(updatedRow.packaging_included);
          if (!isNaN(parsed)) {
            packagingIncluded = parsed;
          } else {
            // If not a valid number, try to find matching option by value string
            const matchingOption = packagingDropdown.find(
              (option) => String(option.value) === updatedRow.packaging_included || String(option.qty) === updatedRow.packaging_included
            );
            if (matchingOption) {
              packagingIncluded = matchingOption.qty || matchingOption.value || 0;
            }
          }
        }
      }
      
      // Final fallback: if still 0 or undefined, try to find matching option
      // This handles cases where the dropdown passes something unexpected
      if ((packagingIncluded === 0 || packagingIncluded === undefined) && updatedRow.packaging_included !== null && updatedRow.packaging_included !== undefined) {
        const matchingOption = packagingDropdown.find(
          (option) => {
            const inputValue = updatedRow.packaging_included;
            return option.value === inputValue || 
                   option.qty === inputValue ||
                   String(option.value) === String(inputValue) ||
                   String(option.qty) === String(inputValue);
          }
        );
        if (matchingOption) {
          packagingIncluded = matchingOption.qty || matchingOption.value || 0;
        }
      }

      updateTransportToManufacturingLCA({
        productId,
        transportId: updatedRow.id,
        transport_means: transportMeans,
        departure_city: updatedRow.departure_city || null,
        departure_country: departureCountry,
        destination_city: updatedRow.destination_city || null,
        destination_country: destinationCountry,
        transport_distance: updatedRow.transport_distance || 0,
        fu_transported: updatedRow.fu_transported || 0,
        packaging_included: packagingIncluded,
        manual_emission_factor: updatedRow.manual_emission_factor || null,
        objects: data,
        setObjects: setData,
      });
    },
    [productId, data, countryDropdown]
  );

  const handleDelete = useCallback(
    (rowId) => {
      deleteTransportToManufacturingLCA({
        productId,
        transportId: rowId,
        callback: fetchData,
      });
    },
    [productId, fetchData, packagingDropdown, countryDropdown]
  );

  const dynamicPlaceholders = useMemo(
    () => ({
      transport_means: unitTransportMeans[data.find((d) => d.id)?.id] || "[xx]",
    }),
    [unitTransportMeans, data]
  );

  const columns = useMemo(
    () => [
      {
        id: "transport_means",
        accessor: "transport_means",
        header: "Means of transportation *",
        type: "dropdown",
        placeholder: "Select means...",
        isCategory: true,
        unit: (rowId, rowData) =>
          transportMeansUnitFromRow(
            rowData,
            transportMeansDropdown,
            unitTransportMeans,
            rowId,
            "transport_means"
          ),
      },
      {
        id: "departure_city",
        accessor: "departure_city",
        header: "City of departure",
        type: "text",
        placeholder: "User types...",
      },
      {
        id: "departure_country",
        accessor: "departure_country",
        header: "Country",
        type: "dropdown",
        placeholder: "Select country...",
      },
      {
        id: "destination_city",
        accessor: "destination_city",
        header: "City of destination",
        type: "text",
        placeholder: "User types...",
      },
      {
        id: "destination_country",
        accessor: "destination_country",
        header: "Country",
        type: "dropdown",
        placeholder: "Select country...",
      },
      {
        id: "transport_distance",
        accessor: "transport_distance",
        header: "Transport distance *",
        type: "number",
        placeholder: "User types...",
        unit: () => "km",
      },
      {
        id: "fu_transported",
        accessor: "fu_transported",
        header: "How much of the FU is transported? *",
        type: "number",
        placeholder: "User types...",
        unit: () => "kg",
      },
      {
        id: "packaging_included",
        accessor: "packaging_included",
        header: "How much packaging is included in the ingoing transport from A1? *",
        type: "dropdown",
        placeholder: "Select or type...",
        unit: () => "kg",
      },
      {
        id: "manual_emission_factor",
        accessor: "manual_emission_factor",
        header: "Manual: xx/tkm (e.g. for specific means of transportation)",
        type: "number",
        placeholder: "User types...",
        optional: true,
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
            <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true,
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          // Backend returns impact categories directly on the object (e.g., global_warming)
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      currentImpactCategory,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
      unitTransportMeans,
      transportMeansDropdown,
      data,
      currentImpactCategoryIndex,
    ]
  );

  const impactCategoryAccessor = currentImpactCategory.key;

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true, // Make impact category read-only
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: {
        label: `Total ${currentImpactCategory.name}`,
      },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      transport_means: transportMeansDropdown || [],
      departure_country: countryDropdown || [],
      destination_country: countryDropdown || [],
      packaging_included: packagingDropdown || [],
    }),
    [transportMeansDropdown, countryDropdown, packagingDropdown]
  );

  return (
    <div>
      <CustomTable
        data={data}
        setData={setData}
        columns={columns || []}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        hiddenColumns={hiddenColumns}
        toggleColumnVisibility={toggleColumnVisibility}
        dropdownData={memoizedDropdownData}
        disableRules={disableRules}
        subtotalColumns={subtotalColumns}
        customInputs={useMemo(
          () => ({ [impactCategoryAccessor]: CustomInputComponent }),
          [impactCategoryAccessor]
        )}
        comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      />
    </div>
  );
};

export default TransportToManufacturingTable;
